/* ──────────────────────────────────────────────────────────────
   ds/components/Toast.jsx — app-wide toast notifications (full)
   ──────────────────────────────────────────────────────────────
   Scroll-proof, position-fixed popups (top-center). Replaces inline
   <Alert> banners that scroll out of view.

   Setup (once, at app root — like ToolbarBusProvider):
     import { ToastProvider } from "./ds";
     <ToastProvider><App /></ToastProvider>

   Use:
     import { useToast } from "./ds";
     const toast = useToast();
     toast.success("Saved.");
     toast.error("Failed.", { title: "Save error", description: "DIV_CODE missing." });
     toast.info("Heads up…", { action: { label: "View", onClick: () => nav() } });
     const id = toast.loading("Saving…");
     toast.update(id, "success", "Saved.");     // update in place
     await toast.promise(savePO(), { loading:"Saving…", success:"Saved", error:"Failed" });

   Features:
     • success/info/warning auto-dismiss (~4s); error sticky (until closed)
     • loading variant (spinner, sticky) + update()/promise() lifecycle
     • rich content: title + description (or a plain message string)
     • action button (e.g. View / Undo)
     • pause-on-hover (timer pauses while hovered)
     • progress bar showing time-to-dismiss
     • dedupe (same variant+text within 4s → refreshed, not duplicated)
     • max visible (queue the rest), newest on top
     • top-center, fixed, z 11000 (above modals), --ds-* tokens
   ────────────────────────────────────────────────────────────── */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

const ToastContext = createContext(null);

const DEFAULTS = { success: 4000, info: 4000, warning: 4000, error: 0, loading: 0 }; // 0 = sticky
const MAX_VISIBLE = Infinity; // unlimited — all toasts stack

const VARIANT = {
  success: { icon: "bi-check-circle-fill", fg: "var(--ds-success)",  bg: "var(--ds-success-subtle)",  border: "var(--ds-success)"  },
  error:   { icon: "bi-exclamation-triangle-fill", fg: "var(--ds-danger)", bg: "var(--ds-danger-subtle)", border: "var(--ds-danger)" },
  info:    { icon: "bi-info-circle-fill", fg: "var(--ds-info)",     bg: "var(--ds-info-subtle)",     border: "var(--ds-info)"     },
  warning: { icon: "bi-exclamation-circle-fill", fg: "var(--ds-warning)", bg: "var(--ds-warning-subtle)", border: "var(--ds-warning)" },
  loading: { icon: "spinner", fg: "var(--ds-primary)", bg: "var(--ds-primary-subtle)", border: "var(--ds-primary)" },
};

let _seq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]); // {id,variant,message,title,description,duration,action,createdAt,_dupKey}
  const timers = useRef({});   // id → { timeout, remaining, startedAt }

  const clearTimer = (id) => {
    const t = timers.current[id];
    if (t && t.timeout) clearTimeout(t.timeout);
    delete timers.current[id];
  };

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    clearTimer(id);
  }, []);

  const startTimer = useCallback((id, duration) => {
    if (!duration || duration <= 0) return;
    const startedAt = Date.now();
    timers.current[id] = {
      remaining: duration, startedAt,
      timeout: setTimeout(() => dismiss(id), duration),
    };
  }, [dismiss]);

  const pause = useCallback((id) => {
    const t = timers.current[id];
    if (!t || !t.timeout) return;
    clearTimeout(t.timeout);
    t.remaining = Math.max(0, t.remaining - (Date.now() - t.startedAt));
    t.timeout = null;
  }, []);

  const resume = useCallback((id) => {
    const t = timers.current[id];
    if (!t || t.timeout || !t.remaining) return;
    t.startedAt = Date.now();
    t.timeout = setTimeout(() => dismiss(id), t.remaining);
  }, [dismiss]);

  const push = useCallback((variant, message, opts = {}) => {
    const duration = opts.duration != null ? opts.duration : (DEFAULTS[variant] ?? 4000);
    const dupKey = `${variant}::${opts.title || ""}::${message || opts.description || ""}`;
    const now = Date.now();
    let reusedId = null;

    setToasts((list) => {
      // dedupe: same content within 4s → refresh that toast instead of adding
      const existing = list.find((t) => t._dupKey === dupKey && now - t.createdAt < 4000);
      if (existing) {
        reusedId = existing.id;
        return list.map((t) => t.id === existing.id ? { ...t, createdAt: now, count: (t.count || 1) + 1 } : t);
      }
      const id = ++_seq;
      reusedId = id;
      const item = {
        id, variant, message,
        title: opts.title || null,
        description: opts.description || null,
        duration, action: opts.action || null,
        createdAt: now, _dupKey: dupKey, count: 1,
      };
      // newest on top; enforce max visible (extras remain in state but hidden by slice at render)
      return [item, ...list];
    });

    // (re)start timer
    clearTimer(reusedId);
    startTimer(reusedId, duration);
    return reusedId;
  }, [startTimer]);

  const update = useCallback((id, variant, message, opts = {}) => {
    const duration = opts.duration != null ? opts.duration : (DEFAULTS[variant] ?? 4000);
    setToasts((list) => list.map((t) => t.id === id
      ? { ...t, variant, message, title: opts.title ?? t.title, description: opts.description ?? t.description,
          action: opts.action ?? t.action, duration, createdAt: Date.now() }
      : t));
    clearTimer(id);
    startTimer(id, duration);
    return id;
  }, [startTimer]);

  const promise = useCallback(async (p, msgs = {}) => {
    const id = push("loading", msgs.loading || "Working…", { duration: 0 });
    try {
      const res = await (typeof p === "function" ? p() : p);
      const okMsg = typeof msgs.success === "function" ? msgs.success(res) : (msgs.success || "Done");
      update(id, "success", okMsg);
      return res;
    } catch (e) {
      const errMsg = typeof msgs.error === "function" ? msgs.error(e) : (msgs.error || "Failed");
      update(id, "error", errMsg, { duration: 0 });
      throw e;
    }
  }, [push, update]);

  const api = useRef({});
  api.current.success = (m, o) => push("success", m, o);
  api.current.error   = (m, o) => push("error", m, o);
  api.current.info    = (m, o) => push("info", m, o);
  api.current.warning = (m, o) => push("warning", m, o);
  api.current.loading = (m, o) => push("loading", m, { duration: 0, ...o });
  api.current.show    = (v, m, o) => push(v, m, o);
  api.current.update  = update;
  api.current.promise = promise;
  api.current.dismiss = dismiss;
  api.current.clear   = () => { Object.keys(timers.current).forEach(clearTimer); setToasts([]); };
  api.current._pause  = pause;
  api.current._resume = resume;

  useEffect(() => () => { Object.values(timers.current).forEach((t) => t.timeout && clearTimeout(t.timeout)); }, []);

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <ToastViewport toasts={toasts} onClose={dismiss} onPause={pause} onResume={resume} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onClose, onPause, onResume }) {
  if (!toasts.length) return null;
  const visible = toasts.slice(0, MAX_VISIBLE);
  const hiddenCount = toasts.length - visible.length;
  return (
    <div
      aria-live="polite"
      style={{
        position: "fixed", top: "var(--ds-sp-3, 12px)", left: "50%", transform: "translateX(-50%)",
        zIndex: 11000, display: "flex", flexDirection: "column", gap: "var(--ds-sp-2, 8px)",
        alignItems: "center", pointerEvents: "none", maxWidth: "92vw", width: "auto",
      }}
    >
      {visible.map((t) => {
        const v = VARIANT[t.variant] || VARIANT.info;
        const showProgress = t.duration > 0;
        const isError = t.variant === "error";
        return (
          <div
            key={t.id}
            role={isError ? "alert" : "status"}
            onMouseEnter={() => onPause(t.id)}
            onMouseLeave={() => onResume(t.id)}
            style={{
              pointerEvents: "auto", position: "relative", overflow: "hidden",
              minWidth: 340, maxWidth: "min(620px, 92vw)",
              display: "flex", alignItems: "flex-start", gap: "var(--ds-sp-3, 12px)",
              padding: "14px 16px",
              background: v.bg,                                   // filled colored tint
              border: `1.5px solid ${v.border}`,
              borderLeft: `6px solid ${v.border}`,               // bold accent
              borderRadius: "var(--ds-radius-md, 10px)",
              boxShadow: "0 10px 34px rgba(0,0,0,.22)",           // stronger elevation
              fontSize: "var(--ds-fs-md, 1rem)", color: "var(--ds-text, #111)",
              animation: "dsToastIn .22s cubic-bezier(.2,.9,.3,1.2)",
            }}
          >
            {/* icon in a colored chip — big, attention-grabbing */}
            <div style={{
              flex: "0 0 auto", width: 34, height: 34, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "var(--ds-surface, #fff)", border: `2px solid ${v.border}`, marginTop: 1,
            }}>
              {v.icon === "spinner"
                ? <span className="spinner-border spinner-border-sm" style={{ color: v.fg }} role="status" />
                : <i className={`bi ${v.icon}`} style={{ color: v.fg, fontSize: "1.25rem", lineHeight: 1 }} />}
            </div>

            {/* content */}
            <div style={{ flex: "1 1 auto", minWidth: 0, paddingTop: 1 }}>
              {t.title && <div style={{ fontWeight: "var(--ds-fw-bold, 700)", fontSize: "var(--ds-fs-md, 1rem)", color: v.fg, marginBottom: 3 }}>{t.title}</div>}
              {t.message && <div style={{ fontWeight: t.title ? "var(--ds-fw-normal,400)" : "var(--ds-fw-semibold,600)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4 }}>{t.message}{t.count > 1 ? `  (×${t.count})` : ""}</div>}
              {t.description && <div style={{ color: "var(--ds-text-muted, #555)", fontSize: "var(--ds-fs-sm, .875rem)", marginTop: 3, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4 }}>{t.description}</div>}

              {/* action row: custom action + (for errors) an explicit OK */}
              {(t.action || isError) && (
                <div style={{ display: "flex", gap: "var(--ds-sp-2, 8px)", marginTop: 10, flexWrap: "wrap" }}>
                  {t.action && (
                    <button
                      type="button"
                      onClick={() => { try { t.action.onClick && t.action.onClick(); } finally { onClose(t.id); } }}
                      style={{
                        background: v.fg, border: `1px solid ${v.border}`, color: "#fff",
                        borderRadius: "var(--ds-radius-sm, 6px)", fontSize: "var(--ds-fs-sm, .875rem)",
                        padding: "4px 14px", cursor: "pointer", fontWeight: 600,
                      }}
                    >
                      {t.action.label}
                    </button>
                  )}
                  {isError && (
                    <button
                      type="button"
                      onClick={() => onClose(t.id)}
                      style={{
                        background: "var(--ds-danger, #dc3545)", border: "none", color: "#fff",
                        borderRadius: "var(--ds-radius-sm, 6px)", fontSize: "var(--ds-fs-sm, .875rem)",
                        padding: "4px 18px", cursor: "pointer", fontWeight: 700,
                      }}
                    >
                      OK
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* close (✕) — always present, clear target */}
            <button
              type="button" onClick={() => onClose(t.id)} aria-label="Close"
              style={{ flex: "0 0 auto", background: "none", border: "none", cursor: "pointer",
                color: v.fg, fontSize: "1.1rem", lineHeight: 1, padding: 4, marginLeft: 4, opacity: .8 }}
            >
              <i className="bi bi-x-lg" />
            </button>

            {/* progress bar */}
            {showProgress && (
              <div style={{ position: "absolute", left: 0, bottom: 0, height: 4, width: "100%", background: "transparent" }}>
                <div key={t.createdAt} style={{
                  height: "100%", background: v.border, width: "100%",
                  transformOrigin: "left", animation: `dsToastBar ${t.duration}ms linear forwards`,
                }} />
              </div>
            )}
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <div style={{ pointerEvents: "auto", fontSize: "var(--ds-fs-xs,.75rem)", color: "var(--ds-text-muted,#666)",
          background: "var(--ds-surface,#fff)", borderRadius: "var(--ds-radius-sm,6px)", padding: "2px 10px",
          boxShadow: "var(--ds-shadow-sm, 0 2px 8px rgba(0,0,0,.12))" }}>
          +{hiddenCount} more
        </div>
      )}
      <style>{`
        @keyframes dsToastIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dsToastBar{from{transform:scaleX(1)}to{transform:scaleX(0)}}
      `}</style>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      success: (m) => console.log("[toast.success]", m),
      error: (m) => console.error("[toast.error]", m),
      info: (m) => console.log("[toast.info]", m),
      warning: (m) => console.warn("[toast.warning]", m),
      loading: (m) => { console.log("[toast.loading]", m); return 0; },
      show: (v, m) => console.log(`[toast.${v}]`, m),
      update: () => {}, promise: async (p) => (typeof p === "function" ? p() : p),
      dismiss: () => {}, clear: () => {},
    };
  }
  return ctx;
}

export default ToastProvider;
